import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["navMarker", "errorMarker", "title", "description", "use", "showDescription", "appearsIn", "showOverview", "size", "onItemClick", "direction", "resultTypes", "renderWrapper"];
/**
 * ============================================================================
 * MIGRATED FILE — DO NOT EDIT
 * ============================================================================
 *
 * This file has been migrated to academy-content-ui.
 * Canonical source: academy-content-ui/static/js/features/recommendations/components/RecommendationsForLearningContent.tsx
 *
 * This .migrated copy is retained only for LearningCenterUI consumers that
 * have not yet moved to academy-content-ui. All changes should be made in
 * academy-content-ui first, then synced here if still needed.
 *
 * This copy will be deleted when LearningCenterUI is shut down.
 *
 * @migrated-to academy-content-ui
 * ============================================================================
 */
import Raven from 'raven-js';
import { useCallback, useRef, useState } from 'react';
import { View } from 'foundations-components/components/View';
import getLang from 'academy-common-lib/utils/getLang';
import { useLearningContentCardModels } from 'academy-common-lib/features/cards/hooks/useLearningContentCardModels';
import { usePostRankedRecommendationsFeedback } from 'academy-common-lib/features/recommendations/hooks/usePostRankedRecommendationsFeedback';
import { useRankedRecommendationsContent } from 'academy-common-lib/features/recommendations/hooks/useRankedRecommendationsContent';
import { useRecommendationsState } from '../recommendationsStore.migrated';
import { useAiRecommendationsForLearningContent } from '../../academyAi';
import { CardsSection } from 'academy-common-lib/features/cardsSection/components/CardsSection';
import { jsx as _jsx } from "react/jsx-runtime";
const useRecommendationsForShorts = ({
  item,
  maxCards,
  skip
}) => {
  const {
    recommendations,
    recommendationsLoading,
    recommendationsError
  } = useAiRecommendationsForLearningContent({
    item,
    size: maxCards,
    skip: !maxCards || skip
  });

  // retrieves hydrated card models for the elements to render
  const {
    cardModels,
    cardModelsError,
    cardModelsLoading
  } = useLearningContentCardModels({
    items: (recommendations === null || recommendations === void 0 ? void 0 : recommendations.map(({
      recommendationId,
      recommendationType,
      language
    }) => {
      return {
        id: recommendationId,
        type: recommendationType,
        language: language || getLang()
      };
    })) || [],
    skip: !(recommendations !== null && recommendations !== void 0 && recommendations.length)
  });
  return {
    recommendationsForShorts: cardModels,
    recommendationsForShortsLoading: recommendationsLoading || cardModelsLoading || maxCards === undefined,
    recommendationsForShortsError: recommendationsError || cardModelsError
  };
};
const useRecommendationsForLearningContent = ({
  item,
  maxCards,
  types: resultTypes
}) => {
  const isShort = item.type === 'SHORT_FORM_CONTENT';

  // shorts uses the similarity recommendations CHIRP API
  const {
    recommendationsForShorts,
    recommendationsForShortsLoading,
    recommendationsForShortsError
  } = useRecommendationsForShorts({
    item,
    maxCards,
    skip: !isShort
  });

  // courses and lessons recommendations uses the ranked recommendations API
  const {
    content: rankedRecommendations,
    loading: rankedRecommendationsLoading,
    error: rankedRecommendationsError,
    correlationId: rankedRecommendationsCorrelationId
  } = useRankedRecommendationsContent({
    language: item.language,
    maxCards,
    types: resultTypes,
    inferenceSource: item.type === 'TRACK' ? 'COURSE_RECOMMENDATIONS' : 'LESSON_RECOMMENDATIONS',
    skip: isShort
  });
  if (isShort) {
    return {
      content: recommendationsForShorts,
      recommendationsError: recommendationsForShortsError,
      recommendationsLoading: recommendationsForShortsLoading,
      correlationId: undefined
    };
  } else {
    return {
      content: rankedRecommendations,
      recommendationsError: rankedRecommendationsError,
      recommendationsLoading: rankedRecommendationsLoading,
      correlationId: rankedRecommendationsCorrelationId
    };
  }
};

/**
 * @migrated-to academy-content-ui
 * @deprecated
 * This file has been migrated to academy-content-ui.
 * Canonical source: academy-content-ui/static/js/features/recommendations/components/RecommendationsForLearningContent.tsx
 *
 * This .migrated copy is retained only for LearningCenterUI consumers that
 * have not yet moved to academy-content-ui. All changes should be made in
 * academy-content-ui first, then synced here if still needed.
 *
 * This copy will be deleted when LearningCenterUI is shut down.
 */
export const RecommendationsForLearningContent = _ref => {
  let {
      navMarker = 'RECOMMENDATIONS_FOR_ENTITY_LOADED',
      errorMarker = 'RECOMMENDATIONS_FOR_ENTITY_API_ERROR',
      title,
      description,
      use,
      showDescription,
      appearsIn,
      showOverview,
      size = 'large',
      onItemClick,
      direction,
      resultTypes,
      renderWrapper
    } = _ref,
    item = _objectWithoutPropertiesLoose(_ref, _excluded);
  const [maxCards, setMaxCards] = useState();
  const {
    content,
    recommendationsError,
    recommendationsLoading,
    correlationId
  } = useRecommendationsForLearningContent({
    item,
    maxCards,
    types: resultTypes
  });
  const {
    postRankedRecommendationsFeedback
  } = usePostRankedRecommendationsFeedback();
  const {
    setLastClickedRecommendationItem
  } = useRecommendationsState();
  const handleOnItemClick = useCallback((cardModel, index) => {
    // VaaS APIs (shorts) don't need to send feedback for the clicked or engaged item, only ranked recommendations do
    if (correlationId && content && item.type !== 'SHORT_FORM_CONTENT') {
      postRankedRecommendationsFeedback({
        entityType: cardModel.type,
        entityId: cardModel.id,
        correlationId,
        content: content
      }).catch(Raven.captureException.bind(Raven));
      setLastClickedRecommendationItem({
        correlationId,
        item: {
          id: cardModel.id,
          type: cardModel.type
        },
        recommendationEngine: 'ranked'
      });
    }
    onItemClick === null || onItemClick === void 0 || onItemClick(cardModel, index, correlationId);
  }, [correlationId, content, item.type, onItemClick, postRankedRecommendationsFeedback, setLastClickedRecommendationItem]);
  const hasContent = !recommendationsLoading && !!(content !== null && content !== void 0 && content.length);
  const hasEverHadContentRef = useRef(false);
  if (hasContent) {
    hasEverHadContentRef.current = true;
  }
  const cardsSection = /*#__PURE__*/_jsx(CardsSection, {
    appearsIn: appearsIn || 'recommendations-for-entity',
    content: content,
    description: description,
    direction: direction,
    error: recommendationsError,
    errorMarker: errorMarker,
    header: title,
    isLoading: recommendationsLoading,
    maxSkeletonListLength: maxCards,
    navMarker: navMarker,
    onItemClick: handleOnItemClick,
    setMaxCards: setMaxCards,
    showDescription: showDescription,
    showOverview: showOverview,
    size: size,
    use: use
  });

  /**
   * We only want to apply `renderWrapper` if there is content to render.
   * `overflow="hidden" visibility="hidden"` is required to render the components into the DOM, allowing them to calculate how much space is available, without rendering the actual component
   */
  if (renderWrapper) {
    if (hasEverHadContentRef.current) {
      return renderWrapper(cardsSection);
    }
    return /*#__PURE__*/_jsx(View, {
      blockSize: "0",
      overflow: "hidden",
      visibility: "hidden",
      children: cardsSection
    });
  }
  return cardsSection;
};